import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";

/**
 * What the on-screen keyboard leaves behind.
 *
 * Returns:
 *   keyboardHeight — how much of the screen the keyboard covers, px
 *   viewportHeight — how tall the usable area is, px
 *
 * On native the height comes from the Capacitor Keyboard plugin, not from the
 * DOM, because inside this app's WebView the DOM does not know the keyboard
 * exists. Measured on an iPhone with the keyboard up:
 *
 *     window.innerHeight        874
 *     visualViewport.height     874   ← unchanged, though ~345pt were covered
 *
 * So `innerHeight - visualViewport.height` is 0 no matter what, and anything
 * derived from it — a lift, a `100vh` cap — silently does nothing. On plain web
 * visualViewport does report honestly, so it is the source there.
 *
 * The listeners live in module scope rather than in the effect, and the last
 * height is remembered. A component that mounts while the keyboard is ALREADY up
 * — the edit dialog opened from a comment box that was being typed in — gets no
 * `keyboardWillShow` of its own, and would otherwise believe the keyboard is
 * closed.
 */
let keyboardHeight = 0;
const subscribers = new Set();
let attached = false;

const visualViewportKeyboardHeight = () => {
  const viewport = window.visualViewport;
  if (!viewport) return 0;

  return Math.max(
    0,
    Math.round(window.innerHeight - viewport.height - viewport.offsetTop),
  );
};

const publish = (px) => {
  const next = Math.max(0, Math.round(Number(px) || 0));
  if (next === keyboardHeight) return;

  keyboardHeight = next;
  subscribers.forEach((notify) => notify(next));
};

// Attached once and never torn down: four listeners for the life of the app, and
// the cached height stays true even while nothing is subscribed.
const attach = () => {
  if (attached) return;
  attached = true;

  const onViewportChange = () => {
    // Native reports the real number; the viewport there is the misleading one.
    if (Capacitor.isNativePlatform()) return;
    publish(visualViewportKeyboardHeight());
  };

  window.visualViewport?.addEventListener("resize", onViewportChange);
  window.visualViewport?.addEventListener("scroll", onViewportChange);
  onViewportChange();

  if (!Capacitor.isNativePlatform()) return;

  // Both show events: Android reports the final height only on the did event.
  const onShow = (info) => publish(info?.keyboardHeight || 0);
  const onHide = () => publish(0);

  try {
    Keyboard.addListener("keyboardWillShow", onShow);
    Keyboard.addListener("keyboardDidShow", onShow);
    Keyboard.addListener("keyboardWillHide", onHide);
    Keyboard.addListener("keyboardDidHide", onHide);
  } catch {
    /* plugin unavailable — callers fall back to a 0 height, as before */
  }
};

export function useKeyboardHeight(active = true) {
  const [height, setHeight] = useState(() => (active ? keyboardHeight : 0));

  useEffect(() => {
    attach();

    if (!active) {
      setHeight(0);
      return undefined;
    }

    setHeight(keyboardHeight);
    subscribers.add(setHeight);

    return () => {
      subscribers.delete(setHeight);
    };
  }, [active]);

  // What is left once the keyboard has taken its share. Derived rather than
  // measured, for the reason in the comment above.
  const viewportHeight = Math.max(
    0,
    (typeof window !== "undefined" ? window.innerHeight : 0) - height,
  );

  return { keyboardHeight: height, viewportHeight };
}

export default useKeyboardHeight;
